import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Typography, Grid, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, InputAdornment, IconButton, Tooltip,
  LinearProgress, Collapse, Paper, Button, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Alert,
} from '@mui/material';
import {
  Search,
  Refresh,
  ExpandMore,
  ExpandLess,
  WarningAmber,
  ErrorOutline,
  CheckCircle,
  InfoOutlined,
  Storefront,
  LocalHospital,
  Inventory,
  TrendingDown,
  CalendarMonth,
} from '@mui/icons-material';
import { loadStockAnalysis, loadInventoryBatches } from '../BaseURL';

// ── Theme tokens (mirrors Home.js) ─────────────────────────────────────────
const PRIMARY      = '#41a386';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

// ── Helper colour maps ─────────────────────────────────────────────────────
const LEVEL = {
  critical: { color: '#dc2626', tint: 'rgba(220,38,38,0.10)', strong: '#991B1B', label: 'Critical', Icon: ErrorOutline },
  warning:  { color: '#d97706', tint: 'rgba(217,119,6,0.10)', strong: '#92400E', label: 'Warning',  Icon: WarningAmber },
  notice:   { color: '#ca8a04', tint: 'rgba(202,138,4,0.10)', strong: '#854D0E', label: 'Notice',   Icon: InfoOutlined },
  ok:       { color: '#10b981', tint: 'rgba(16,185,129,0.10)', strong: '#15803D', label: 'OK',     Icon: CheckCircle },
};

// ── Status pill (mirrors Home.js inline-flex pill) ─────────────────────────
const StatusPill = ({ level }) => {
  const lv = LEVEL[level] || LEVEL.ok;
  const Lv = lv.Icon;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.25, borderRadius: 1,
      bgcolor: lv.tint, color: lv.strong,
      fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>
      <Lv sx={{ fontSize: 12 }} />
      {lv.label}
    </Box>
  );
};

// ── Expiry badge (compact, no Chip) ─────────────────────────────────────────
const ExpiryBadge = ({ date }) => {
  if (!date) {
    return (
      <Box sx={{ display: 'inline-flex', px: 1, py: 0.25, borderRadius: 1, bgcolor: '#f1f5f9', color: MUTED, fontSize: '0.7rem', fontWeight: 700 }}>
        No expiry
      </Box>
    );
  }
  const days = Math.round((new Date(date) - new Date()) / 86400000);
  let bg = LEVEL.ok.tint, color = LEVEL.ok.strong, text = new Date(date).toLocaleDateString();
  if (days < 0) {
    bg = LEVEL.critical.tint; color = LEVEL.critical.strong;
    text = `Expired ${new Date(date).toLocaleDateString()}`;
  } else if (days <= 90) {
    bg = LEVEL.warning.tint; color = LEVEL.warning.strong;
    text = `Exp in ${days}d`;
  }
  return (
    <Box sx={{ display: 'inline-flex', px: 1, py: 0.25, borderRadius: 1, bgcolor: bg, color, fontSize: '0.7rem', fontWeight: 700 }}>
      {text}
    </Box>
  );
};

// ── Consumption bar ────────────────────────────────────────────────────────
const ConsumptionBar = ({ pct, alertLevel }) => {
  const color = LEVEL[alertLevel]?.color || LEVEL.ok.color;
  return (
    <Box sx={{ width: '100%', minWidth: 130 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
        <Typography sx={{ fontSize: '0.66rem', color: MUTED, fontWeight: 600 }}>Consumed</Typography>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color }}>{pct}%</Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(pct, 100)}
        sx={{
          height: 6, borderRadius: 3,
          bgcolor: '#f1f5f9',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 }
        }}
      />
    </Box>
  );
};

// ── Stat-tile builder for compact strip ───────────────────────────────────
const StatTile = ({ label, value, icon, tint, color, active, onClick }) => (
  <Paper
    onClick={onClick}
    elevation={0}
    sx={{
      p: 1.5, borderRadius: 2, cursor: onClick ? 'pointer' : 'default',
      border: `1px solid ${active ? color : BORDER}`,
      outline: active ? `2px solid ${color}` : 'none',
      outlineOffset: -1,
      bgcolor: '#fff',
      display: 'flex', alignItems: 'center', gap: 1.5,
      transition: 'border-color 0.15s, transform 0.15s',
      '&:hover': onClick ? { transform: 'translateY(-1px)', borderColor: color } : {},
    }}
  >
    <Box sx={{
      width: 36, height: 36, borderRadius: 1.5,
      bgcolor: tint, color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={LABEL_SX}>{label}</Typography>
      <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: STRONG, lineHeight: 1.1, mt: 0.25 }}>
        {Number(value || 0).toLocaleString()}
      </Typography>
    </Box>
  </Paper>
);

// ── Batch detail row ───────────────────────────────────────────────────────
const BatchRow = ({ batch, idx }) => {
  const days   = batch.days_until_expiry;
  const status = days < 0 ? 'expired' : days <= 90 ? 'soon' : 'ok';
  const tint   = status === 'expired' ? LEVEL.critical.tint
              : status === 'soon'    ? LEVEL.warning.tint
              : 'transparent';
  const strong = status === 'expired' ? LEVEL.critical.strong
              : status === 'soon'    ? LEVEL.warning.strong
              : LEVEL.ok.strong;
  return (
    <TableRow sx={{ bgcolor: tint }}>
      <TableCell sx={{ pl: 4, color: MUTED, fontSize: '0.78rem', fontWeight: 600 }}>Batch {idx + 1}</TableCell>
      <TableCell sx={{ color: MUTED, fontSize: '0.78rem' }}>
        {new Date(batch.purchase_date).toLocaleDateString()}
      </TableCell>
      <TableCell align="center" sx={{ fontWeight: 700, color: STRONG, fontSize: '0.84rem', fontVariantNumeric: 'tabular-nums' }}>
        {parseInt(batch.batch_qty).toLocaleString()}
      </TableCell>
      <TableCell align="right" sx={{ color: STRONG, fontWeight: 600, fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>
        ₵{parseFloat(batch.unit_cost).toFixed(2)}
      </TableCell>
      <TableCell align="center">
        <ExpiryBadge date={batch.expiry_date} />
      </TableCell>
      <TableCell align="center">
        <Box sx={{
          display: 'inline-flex', px: 1, py: 0.25, borderRadius: 1,
          bgcolor: status === 'ok' ? LEVEL.ok.tint : tint,
          color: strong,
          fontSize: '0.7rem', fontWeight: 700,
        }}>
          {status === 'expired' ? 'Expired' : `${days}d left`}
        </Box>
      </TableCell>
    </TableRow>
  );
};

// ── Main component ─────────────────────────────────────────────────────────

// `embedded` (default false) — when true, the component renders without its
// own page header / wrapper padding. Purchase Orders mounts it that way as a
// tab; the standalone Stock Analysis sidebar route keeps the full page chrome.
const StockAnalysis = ({ embedded = false }) => {
  const [data,         setData]         = useState([]);
  const [summary,      setSummary]      = useState({ total: 0, critical: 0, warning: 0, notice: 0, ok: 0 });
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [search,       setSearch]       = useState('');
  const [levelFilter,  setLevelFilter]  = useState('all');
  const [expanded,     setExpanded]     = useState({});

  const [batchDetails, setBatchDetails] = useState({});

  const [page, setPage] = useState(0);
  const [rpp,  setRpp]  = useState(15);

  const formatCurrency = v =>
    new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(v || 0);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    setBatchDetails({});
    setExpanded({});
    try {
      const res = await axios.get(loadStockAnalysis);
      if (res.data.success) {
        setData(res.data.results || []);
        setSummary(res.data.summary || { total: 0, critical: 0, warning: 0, notice: 0, ok: 0 });
      } else {
        throw new Error(res.data.message || 'Failed to load analysis');
      }
    } catch (err) {
      setError(err.message || 'Error loading stock analysis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchBatchDetails = async (code, name) => {
    if (batchDetails[code]?.status === 'loading' || batchDetails[code]?.status === 'loaded') return;

    setBatchDetails(prev => ({ ...prev, [code]: { status: 'loading', rows: [] } }));
    try {
      const res = await axios.get(loadInventoryBatches, { params: { name } });
      if (res.data.success) {
        setBatchDetails(prev => ({ ...prev, [code]: { status: 'loaded', rows: res.data.results } }));
      } else {
        throw new Error(res.data.message || 'Failed to load batches');
      }
    } catch (err) {
      console.error('Error fetching batch details for', code, name, err);
      setBatchDetails(prev => ({ ...prev, [code]: { status: 'error', rows: [], name } }));
    }
  };

  const toggleExpand = (code, name) => {
    const willOpen = !expanded[code];
    setExpanded(prev => ({ ...prev, [code]: willOpen }));
    if (willOpen) fetchBatchDetails(code, name);
  };

  const filtered = useMemo(() => {
    let d = [...data];
    if (search.trim())
      d = d.filter(r =>
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        r.code.toLowerCase().includes(search.toLowerCase())
      );
    if (levelFilter !== 'all')
      d = d.filter(r => r.alertLevel === levelFilter);
    return d;
  }, [data, search, levelFilter]);

  useEffect(() => { setPage(0); }, [search, levelFilter]);

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
      <CircularProgress size={40} sx={{ color: PRIMARY }} />
      <Typography sx={{ mt: 2, fontSize: '0.86rem', fontWeight: 600, color: MUTED }}>Analysing stock levels…</Typography>
    </Box>
  );

  if (error) return (
    <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
  );

  // Standalone-mode header (skipped when embedded inside Purchase Orders)
  const header = !embedded && (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={1.5}
      sx={{ mb: 2 }}
    >
      <Box>
        <Typography sx={{ ...LABEL_SX, color: PRIMARY, mb: 0.25 }}>
          Procurement · Intelligence
        </Typography>
        <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
          Stock Analysis
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25, maxWidth: 760 }}>
          Live consumption and expiry intelligence across every batch on file. Tiles below act as
          filters — tap one to narrow the table to that alert level. Click any product row to expand
          its individual batches, days-until-expiry, and the running totals invested vs. consumed.
        </Typography>
      </Box>
      <Tooltip title="Refresh data" arrow>
        <IconButton
          onClick={fetchData}
          size="small"
          sx={{
            border: `1px solid ${BORDER}`, bgcolor: '#fff', color: MUTED,
            '&:hover': { bgcolor: '#f1f5f9', color: STRONG },
          }}
        >
          <Refresh fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <Box sx={embedded ? undefined : { bgcolor: '#f8fafc', minHeight: '100vh', py: 2.5, px: 3 }}>
      {header}
      {/* Compact stats strip — clickable filters */}
      <Grid container spacing={1.25} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2.4}>
          <StatTile
            label="Tracked" value={summary.total}
            icon={<Inventory fontSize="small" />}
            tint="#f1f5f9" color={STRONG}
          />
        </Grid>
        {['critical', 'warning', 'notice', 'ok'].map(key => {
          const lv = LEVEL[key];
          const Lv = lv.Icon;
          return (
            <Grid item xs={6} md={2.4} key={key}>
              <StatTile
                label={lv.label}
                value={summary[key] || 0}
                icon={<Lv fontSize="small" />}
                tint={lv.tint}
                color={lv.strong}
                active={levelFilter === key}
                onClick={() => setLevelFilter(levelFilter === key ? 'all' : key)}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* Main card */}
      <Paper elevation={0} sx={{
        border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff',
      }}>
        {/* Filter row */}
        <Box sx={{
          p: 1.5,
          borderBottom: `1px solid ${BORDER}`,
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
          bgcolor: '#fff',
        }}>
          <TextField
            size="small"
            placeholder="Search by product name or code…"
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ minWidth: 240, flex: { xs: '1 1 100%', md: '0 0 320px' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Alert Level</InputLabel>
            <Select value={levelFilter} label="Alert Level" onChange={e => setLevelFilter(e.target.value)}>
              <MenuItem value="all">All Levels</MenuItem>
              {['critical', 'warning', 'notice', 'ok'].map(l => (
                <MenuItem key={l} value={l}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: LEVEL[l].color }} />
                    <span>{LEVEL[l].label}</span>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh data" arrow>
            <IconButton
              size="small"
              onClick={fetchData}
              sx={{
                border: `1px solid ${BORDER}`, bgcolor: '#fff', color: MUTED,
                '&:hover': { bgcolor: '#f1f5f9', color: STRONG },
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
            {filtered.length} of {data.length} shown
          </Typography>
        </Box>

        {/* Table */}
        <TableContainer sx={{ maxHeight: '62vh' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, ...LABEL_SX, py: 1.25 } }}>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={{ minWidth: 200 }}>Product</TableCell>
                <TableCell align="center" sx={{ width: 90 }}>Batches</TableCell>
                <TableCell align="center" sx={{ width: 80 }}>
                  <Stack spacing={0.25} alignItems="center">
                    <Storefront sx={{ fontSize: 14, color: '#ea580c' }} />
                    <span>Store</span>
                  </Stack>
                </TableCell>
                <TableCell align="center" sx={{ width: 90 }}>
                  <Stack spacing={0.25} alignItems="center">
                    <LocalHospital sx={{ fontSize: 14, color: '#0284c7' }} />
                    <span>Dispensary</span>
                  </Stack>
                </TableCell>
                <TableCell align="center" sx={{ width: 80 }}>
                  <Stack spacing={0.25} alignItems="center">
                    <Inventory sx={{ fontSize: 14, color: PRIMARY }} />
                    <span>Total</span>
                  </Stack>
                </TableCell>
                <TableCell align="center" sx={{ width: 100 }}>
                  <Stack spacing={0.25} alignItems="center">
                    <TrendingDown sx={{ fontSize: 14, color: '#8b5cf6' }} />
                    <span>Consumed</span>
                  </Stack>
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>Consumption</TableCell>
                <TableCell align="center" sx={{ width: 130 }}>Earliest expiry</TableCell>
                <TableCell align="center" sx={{ width: 110 }}>Status</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.slice(page * rpp, page * rpp + rpp).map(row => {
                const lv     = LEVEL[row.alertLevel] || LEVEL.ok;
                const isOpen = expanded[row.code];

                return (
                  <React.Fragment key={row.code}>
                    {/* Main row */}
                    <TableRow
                      sx={{
                        bgcolor: isOpen ? lv.tint : '#fff',
                        borderLeft: `3px solid ${lv.color}`,
                        '&:hover': { bgcolor: lv.tint },
                        cursor: 'pointer',
                      }}
                      onClick={() => toggleExpand(row.code, row.name)}
                    >
                      <TableCell sx={{ py: 1 }}>
                        <IconButton size="small" sx={{ color: lv.color, p: 0.25 }}>
                          {isOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: STRONG, lineHeight: 1.25 }}>
                          {row.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: MUTED, mt: 0.25 }}>
                          Code: {row.code}
                        </Typography>
                        {row.alerts.length > 0 && (
                          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                            {row.alerts.slice(0, 2).map((a, i) => (
                              <Typography key={i} sx={{ fontSize: '0.72rem', color: lv.strong, fontWeight: 600, display: 'block' }}>
                                • {a}
                              </Typography>
                            ))}
                            {row.alerts.length > 2 && (
                              <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                                +{row.alerts.length - 2} more…
                              </Typography>
                            )}
                          </Stack>
                        )}
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.5,
                          px: 0.85, py: 0.25, borderRadius: 0.85,
                          bgcolor: '#f1f5f9', color: '#475569',
                          fontSize: '0.7rem', fontWeight: 700,
                        }}>
                          {row.batchCount}
                          {row.expiredBatches > 0 && (
                            <Box component="span" sx={{ ml: 0.25, color: LEVEL.critical.strong, fontWeight: 800 }}>
                              · {row.expiredBatches} exp
                            </Box>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: '#ea580c', fontVariantNumeric: 'tabular-nums' }}>
                          {row.storeStock.toLocaleString()}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: '#0284c7', fontVariantNumeric: 'tabular-nums' }}>
                          {row.dispensaryStock.toLocaleString()}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography sx={{
                          fontSize: '0.92rem', fontWeight: 800,
                          color: row.currentTotal === 0 ? LEVEL.critical.strong : LEVEL.ok.strong,
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {row.currentTotal.toLocaleString()}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography sx={{ fontSize: '0.84rem', fontWeight: 700, color: row.consumed > 0 ? '#8b5cf6' : '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                          {row.consumed.toLocaleString()}
                        </Typography>
                        <Typography sx={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                          of {row.totalBatched.toLocaleString()}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ minWidth: 150 }}>
                        <ConsumptionBar pct={row.consumedPct} alertLevel={row.alertLevel} />
                      </TableCell>

                      <TableCell align="center">
                        <ExpiryBadge date={row.earliestExpiry} />
                      </TableCell>

                      <TableCell align="center">
                        <StatusPill level={row.alertLevel} />
                      </TableCell>
                    </TableRow>

                    {/* Expanded batch detail */}
                    <TableRow>
                      <TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ bgcolor: '#f8fafc', px: 2.5, pt: 2, pb: 2.5, borderLeft: `3px solid ${lv.color}`, borderBottom: `1px solid ${BORDER}` }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>

                              {/* Batch breakdown */}
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                                  <CalendarMonth sx={{ fontSize: 14, color: lv.color }} />
                                  <Typography sx={{ ...LABEL_SX, color: STRONG }}>
                                    Batch Details ({row.batchCount} batch{row.batchCount !== 1 ? 'es' : ''})
                                  </Typography>
                                </Stack>
                                <Paper elevation={0} sx={{ borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
                                  {(() => {
                                    const bd = batchDetails[row.code];
                                    if (!bd || bd.status === 'loading') {
                                      return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, py: 3 }}>
                                          <CircularProgress size={18} sx={{ color: lv.color }} />
                                          <Typography sx={{ fontSize: '0.82rem', color: MUTED }}>Loading batch details…</Typography>
                                        </Box>
                                      );
                                    }
                                    if (bd.status === 'error') {
                                      return (
                                        <Box sx={{ py: 2, px: 1.5 }}>
                                          <Alert severity="error" sx={{ borderRadius: 1.5 }}>
                                            Failed to load batch details.{' '}
                                            <Button size="small" onClick={() => {
                                              setBatchDetails(prev => ({ ...prev, [row.code]: { status: 'idle', rows: [] } }));
                                              fetchBatchDetails(row.code, row.name);
                                            }}>Retry</Button>
                                          </Alert>
                                        </Box>
                                      );
                                    }
                                    if (!bd.rows || bd.rows.length === 0) {
                                      return (
                                        <Box sx={{ textAlign: 'center', py: 3, color: '#94a3b8' }}>
                                          <Inventory sx={{ fontSize: 30, mb: 0.5 }} />
                                          <Typography sx={{ fontSize: '0.82rem' }}>No batch records found.</Typography>
                                        </Box>
                                      );
                                    }
                                    return (
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ '& th': { bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, ...LABEL_SX, py: 1 } }}>
                                            <TableCell sx={{ pl: 4 }}>Batch</TableCell>
                                            <TableCell>Purchase date</TableCell>
                                            <TableCell align="center">Qty</TableCell>
                                            <TableCell align="right">Unit cost</TableCell>
                                            <TableCell align="center">Expiry date</TableCell>
                                            <TableCell align="center">Days left</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {bd.rows.map((b, i) => <BatchRow key={b.id || i} batch={b} idx={i} />)}
                                        </TableBody>
                                      </Table>
                                    );
                                  })()}
                                </Paper>
                              </Box>

                              {/* Summary panel */}
                              <Box sx={{ width: { xs: '100%', md: 270 }, flexShrink: 0 }}>
                                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                                  <Inventory sx={{ fontSize: 14, color: lv.color }} />
                                  <Typography sx={{ ...LABEL_SX, color: STRONG }}>Summary</Typography>
                                </Stack>
                                <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1.5, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
                                  {[
                                    { label: 'Total Received',   value: row.totalBatched.toLocaleString() + ' units' },
                                    { label: 'Store',            value: row.storeStock.toLocaleString() + ' units',       color: '#ea580c' },
                                    { label: 'Dispensary',       value: row.dispensaryStock.toLocaleString() + ' units',  color: '#0284c7' },
                                    { label: 'Current Total',    value: row.currentTotal.toLocaleString() + ' units',     color: LEVEL.ok.strong, bold: true },
                                    { label: 'Consumed',         value: row.consumed.toLocaleString() + ' (' + row.consumedPct + '%)', color: '#8b5cf6' },
                                    { label: 'Total Invested',   value: formatCurrency(row.totalInvested) },
                                    { label: 'Current Value',    value: formatCurrency(row.currentTotal * row.unitPrice) },
                                    { label: 'Last Purchase',    value: row.lastPurchaseDate ? new Date(row.lastPurchaseDate).toLocaleDateString() : '—' },
                                  ].map(({ label, value, color, bold }, i, arr) => (
                                    <Stack
                                      key={label}
                                      direction="row" justifyContent="space-between" alignItems="center"
                                      sx={{ py: 0.6, borderBottom: i < arr.length - 1 ? `1px solid ${BORDER}` : 'none' }}
                                    >
                                      <Typography sx={{ fontSize: '0.74rem', color: MUTED, fontWeight: 600 }}>{label}</Typography>
                                      <Typography sx={{ fontSize: '0.78rem', fontWeight: bold ? 800 : 700, color: color || STRONG, fontVariantNumeric: 'tabular-nums' }}>
                                        {value}
                                      </Typography>
                                    </Stack>
                                  ))}

                                  {row.alerts.length > 0 && (
                                    <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${BORDER}` }}>
                                      <Typography sx={{ ...LABEL_SX, color: lv.strong, mb: 0.5 }}>Alerts</Typography>
                                      {row.alerts.map((a, i) => (
                                        <Stack key={i} direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.4 }}>
                                          <Box component="span" sx={{ color: lv.color, fontWeight: 800, lineHeight: 1.3 }}>•</Box>
                                          <Typography sx={{ fontSize: '0.74rem', color: lv.strong, fontWeight: 600 }}>{a}</Typography>
                                        </Stack>
                                      ))}
                                    </Box>
                                  )}
                                </Paper>
                              </Box>
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rpp}
          onRowsPerPageChange={e => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 15, 25, 50]}
          sx={{
            borderTop: `1px solid ${BORDER}`,
            '.MuiTablePagination-toolbar': { minHeight: 44 },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontSize: '0.78rem', color: MUTED,
            },
            '.MuiTablePagination-select': { fontSize: '0.78rem', fontWeight: 700, color: STRONG },
          }}
        />

        {filtered.length === 0 && (
          <Box sx={{ p: 5, textAlign: 'center', borderTop: `1px solid ${BORDER}` }}>
            <Inventory sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: STRONG }}>No products found</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25 }}>
              {data.length === 0
                ? 'No batches recorded yet. Complete a purchase to populate this view.'
                : 'Try adjusting your search or alert level.'}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default StockAnalysis;
